import { useMemo } from 'react';
import { useProviderSetupGuide } from '../../hooks/useIntegrations';
import { Card, CardHeader, MarkdownContent } from '@/components/ui';
import { ExternalLink } from 'lucide-react';
import type { Integration } from '../../types/integrations';
import {
  applyGuideTokens,
  extractCopyMarkers,
  getResolvedTokenValues,
} from '../../lib/integration-guide-tokens';

interface SetupGuidePanelProps {
  provider: string;
  providerDisplayName: string;
  documentationUrl?: string;
  integration?: Integration;
}

export default function SetupGuidePanel({
  provider,
  providerDisplayName,
  documentationUrl,
  integration,
}: SetupGuidePanelProps) {
  const { data: markdown, isLoading, error } = useProviderSetupGuide(provider);

  const guideContext = useMemo(
    () => ({
      integration,
      provider: { name: provider, displayName: providerDisplayName, documentationUrl },
    }),
    [integration, provider, providerDisplayName, documentationUrl],
  );

  const { resolvedMarkdown, copyableValues } = useMemo(() => {
    if (!markdown) {
      return { resolvedMarkdown: markdown, copyableValues: [] as string[] };
    }
    const { markdown: demarked, values: markedCopyables } = extractCopyMarkers(markdown);
    return {
      resolvedMarkdown: applyGuideTokens(demarked, guideContext),
      copyableValues: [
        ...getResolvedTokenValues(guideContext),
        ...markedCopyables.map((v) => applyGuideTokens(v, guideContext)),
      ],
    };
  }, [markdown, guideContext]);

  return (
    <Card>
      <CardHeader
        title={`${providerDisplayName} Setup Guide`}
        description="Follow these steps to configure the webhook in your provider"
      />
      {isLoading && (
        <div className="space-y-2">
          <div className="h-3 bg-muted rounded animate-pulse w-3/4" />
          <div className="h-3 bg-muted rounded animate-pulse w-full" />
          <div className="h-3 bg-muted rounded animate-pulse w-5/6" />
        </div>
      )}
      {error && (
        <p className="text-sm text-red-600 dark:text-red-400">
          Failed to load setup instructions. See the provider documentation for
          configuration details.
        </p>
      )}
      {resolvedMarkdown && (
        <MarkdownContent size="sm" copyableValues={copyableValues}>
          {resolvedMarkdown}
        </MarkdownContent>
      )}
      {documentationUrl && (
        <a
          href={documentationUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-500 mt-4"
        >
          Provider documentation <ExternalLink className="h-3 w-3" />
        </a>
      )}
    </Card>
  );
}
